import type { IEmployeeLocationFilterDto } from "app/pages/sales-intelligence/domain/contacts/types/filter-values";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import { getPlaybookContactsCount } from "../constants";
import type { Playbook } from "../types/playbooks.types";

export const areProfileIdsEqual = (currentIds: string[], initialIds: string[]): boolean => {
    if (currentIds.length !== initialIds.length) {
        return false;
    }

    const sortedCurrent = [...currentIds].sort();
    const sortedInitial = [...initialIds].sort();

    return sortedCurrent.every((id, index) => id === sortedInitial[index]);
};

const sortedArrayEqual = <T extends string | number>(current: readonly T[], initial: readonly T[]): boolean => {
    if (current.length !== initial.length) {
        return false;
    }

    const sortedCurrent = [...current].sort();
    const sortedInitial = [...initial].sort();

    return sortedCurrent.every((value, index) => value === sortedInitial[index]);
};

const normalizeEmployeeLocation = (location?: IEmployeeLocationFilterDto): IEmployeeLocationFilterDto | undefined => {
    if (!location?.codes?.length && !location?.regions?.length) {
        return undefined;
    }

    return {
        inclusion: location.inclusion ?? inclusionOptionMap.includeOnly,
        codes: location.codes ?? [],
        regions: location.regions ?? [],
    };
};

export const areEmployeeLocationsEqual = (
    current?: IEmployeeLocationFilterDto,
    initial?: IEmployeeLocationFilterDto,
): boolean => {
    const normalizedCurrent = normalizeEmployeeLocation(current);
    const normalizedInitial = normalizeEmployeeLocation(initial);

    if (!normalizedCurrent && !normalizedInitial) {
        return true;
    }

    if (!normalizedCurrent || !normalizedInitial) {
        return false;
    }

    return (
        normalizedCurrent.inclusion === normalizedInitial.inclusion &&
        sortedArrayEqual(normalizedCurrent.codes ?? [], normalizedInitial.codes ?? []) &&
        sortedArrayEqual(normalizedCurrent.regions ?? [], normalizedInitial.regions ?? [])
    );
};

type HasPlaybookWizardChangesParams = {
    playbook?: Playbook;
    name: string;
    description: string;
    selectedCadenceId: number | null;
    scoringRuleId: string | null;
    enabledProfileIds: string[];
    contactsCount?: number;
    employeeLocation?: IEmployeeLocationFilterDto;
};

export const hasPlaybookWizardChanges = ({
    playbook,
    name,
    description,
    selectedCadenceId,
    scoringRuleId,
    enabledProfileIds,
    contactsCount,
    employeeLocation,
}: HasPlaybookWizardChangesParams): boolean => {
    const initialName = playbook?.name ?? "";
    const initialDescription = playbook?.description ?? "";
    const initialCadenceId = playbook?.cadence?.id ?? null;
    const initialScoringRuleId = playbook?.scoringRuleId ?? null;
    const initialProfileIds = playbook?.employeeProfilesIds ?? [];
    const initialContactsCount = getPlaybookContactsCount(playbook?.contactsCount);

    return (
        name.trim() !== initialName.trim() ||
        description.trim() !== (initialDescription?.trim() ?? "") ||
        selectedCadenceId !== initialCadenceId ||
        scoringRuleId !== initialScoringRuleId ||
        !areProfileIdsEqual(enabledProfileIds, initialProfileIds) ||
        getPlaybookContactsCount(contactsCount) !== initialContactsCount ||
        !areEmployeeLocationsEqual(employeeLocation, playbook?.employeeLocation)
    );
};
